import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Topbar } from "../components/shell/Topbar";
import { useLayoutContext } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { cn } from "@/packages/utils/cn";

interface Environment {
  id: string;
  name: string;
  key: string;
  isDefault: boolean;
}
interface GlobalFlag {
  id: string;
  projectId: string;
  name: string;
  key: string;
  enabled: boolean;
  rollout: number;
  environment: Environment | null;
}
interface FlagGroup {
  id: string;
  projectId: string;
  name: string;
  key: string;
  prefix: string;
  description: string | null;
  color: string;
}
interface GroupFlag {
  id: string;
  groupId: string;
  name: string;
  key: string;
  defaultEnabled: boolean;
  defaultPercentage: number;
}
interface GroupRecord {
  id: string;
  group: { id: string; name: string };
  name: string | null;
  metadata: Record<string, unknown> | null;
}
interface RecordValue {
  recordId: string;
  groupFlagId: string;
  enabled: boolean;
  percentage: number;
  environmentId: string | null;
}

type SelectionGlobal = { kind: "global" };
type SelectionGroup = { kind: "group"; groupId: string };
type Selection = SelectionGlobal | SelectionGroup;

function derivePrefix(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  return slug.slice(0, 3);
}

export function FeatureFlagsPage() {
  const { teamId, projectId } = useParams();
  const { teams, projects } = useLayoutContext();
  const team = teams.find((t) => t.id === teamId);
  const project = (projects[teamId ?? ""] ?? []).find((p) => p.referenceId === projectId);

  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [envKey, setEnvKey] = useState<string | null>(null);
  const [globals, setGlobals] = useState<GlobalFlag[]>([]);
  const [groups, setGroups] = useState<FlagGroup[]>([]);
  const [selection, setSelection] = useState<Selection>({ kind: "global" });
  const [groupFlags, setGroupFlags] = useState<GroupFlag[]>([]);
  const [records, setRecords] = useState<GroupRecord[]>([]);
  const [recordValues, setRecordValues] = useState<
    Record<string, RecordValue[]>
  >({});

  const [flagsLoading, setFlagsLoading] = useState(false);
  const [groupDetailsLoading, setGroupDetailsLoading] = useState(false);

  const [showCreateFlag, setShowCreateFlag] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showCreateGroupFlag, setShowCreateGroupFlag] = useState(false);
  const [showCreateEnv, setShowCreateEnv] = useState(false);
  const [showCreateRecord, setShowCreateRecord] = useState(false);

  const refreshEnvs = () => {
    if (!projectId) return;
    fetch(`/api/projects/${projectId}/environments`)
      .then((r) => r.json())
      .then((d: Environment[]) => {
        setEnvironments(d);
        if (!envKey) {
          const def = d.find((e) => e.isDefault) ?? d[0];
          if (def) setEnvKey(def.key);
        }
      });
  };

  const refreshFlags = async () => {
    if (!projectId) return;
    setFlagsLoading(true);
    try {
      const q = envKey ? `?env=${envKey}` : "";
      const [flagsRes, groupsRes] = await Promise.all([
        fetch(`/api/projects/${projectId}/flags${q}`).then((r) => r.json()),
        fetch(`/api/projects/${projectId}/groups`).then((r) => r.json()),
      ]);
      setGlobals(flagsRes);
      setGroups(groupsRes);
    } finally {
      setFlagsLoading(false);
    }
  };

  const refreshGroupDetails = async (groupId: string) => {
    setGroupDetailsLoading(true);
    try {
      const [flags, recs] = await Promise.all([
        fetch(`/api/projects/${projectId}/groups/${groupId}/flags`).then((r) =>
          r.json(),
        ),
        fetch(`/api/projects/${projectId}/groups/${groupId}/records`).then((r) =>
          r.json(),
        ),
      ]);
      setGroupFlags(flags);
      setRecords(recs);

      const valueMap: Record<string, RecordValue[]> = {};
      await Promise.all(
        recs.map(async (rec: GroupRecord) => {
          const q = envKey ? `?env=${envKey}` : "";
          const vals = await fetch(
            `/api/projects/${projectId}/records/${rec.id}/values${q}`,
          ).then((r) => r.json());
          valueMap[rec.id] = vals;
        }),
      );
      setRecordValues(valueMap);
    } finally {
      setGroupDetailsLoading(false);
    }
  };

  useEffect(() => {
    refreshEnvs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (!envKey) return;
    refreshFlags();
    if (selection.kind === "group") refreshGroupDetails(selection.groupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envKey, selection]);

  if (!team || !project) return null;

  const currentGroup =
    selection.kind === "group"
      ? groups.find((g) => g.id === selection.groupId)
      : null;

  const handleAddFlag = () => {
    if (selection.kind === "global") setShowCreateFlag(true);
    else setShowCreateGroupFlag(true);
  };

  const updateFlagOptimistic = async (
    flag: GlobalFlag,
    patch: Partial<GlobalFlag>,
  ) => {
    const prev = globals;
    setGlobals((curr) =>
      curr.map((f) => (f.id === flag.id ? { ...f, ...patch } : f)),
    );
    try {
      const res = await fetch(`/api/projects/${projectId}/flags/${flag.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error("Failed");
    } catch {
      setGlobals(prev);
    }
  };

  const deleteFlag = async (flag: GlobalFlag) => {
    if (!confirm("Delete this flag?")) return;
    await fetch(`/api/projects/${projectId}/flags/${flag.id}`, {
      method: "DELETE",
    });
    refreshFlags();
  };

  const setRecordValueOptimistic = async (
    recordId: string,
    groupFlagId: string,
    enabled: boolean,
    percentage: number,
  ) => {
    if (selection.kind !== "group") return;
    const prev = recordValues;
    setRecordValues((curr) => {
      const list = curr[recordId] ?? [];
      const idx = list.findIndex((v) => v.groupFlagId === groupFlagId);
      const next: RecordValue = {
        recordId,
        groupFlagId,
        enabled,
        percentage,
        environmentId:
          environments.find((e) => e.key === envKey)?.id ?? null,
      };
      const newList =
        idx >= 0
          ? list.map((v, i) => (i === idx ? next : v))
          : [...list, next];
      return { ...curr, [recordId]: newList };
    });
    try {
      const res = await fetch(
        `/api/projects/${projectId}/groups/${selection.groupId}/records/${recordId}/values/${groupFlagId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            enabled,
            rollout: percentage,
            environmentKey: envKey,
          }),
        },
      );
      if (!res.ok) throw new Error("Failed");
    } catch {
      setRecordValues(prev);
    }
  };

  return (
    <>
      <Topbar
        eyebrow="Feature flags"
        title="Feature flags"
        crumbs={[
          { label: team.name, to: `/teams/${team.id}` },
          { label: project.name, to: `/teams/${team.id}/projects/${project.referenceId}` },
          { label: "Flags" },
        ]}
        actions={
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCreateEnv(true)}
            >
              + Environment
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCreateGroup(true)}
            >
              + Group
            </Button>
            <Button size="sm" onClick={handleAddFlag}>
              + New flag
            </Button>
          </>
        }
      />

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[280px_1fr] overflow-hidden">
        {/* Folder tree */}
        <aside className="border-r border-rule bg-card-paper overflow-auto py-3">
          <div className="px-4 py-2 text-eyebrow">Global</div>
          <button
            onClick={() => setSelection({ kind: "global" })}
            className={cn(
              "w-full text-left px-4 py-2 flex items-center gap-2 text-sm border-l-2",
              selection.kind === "global"
                ? "bg-panel border-foreground font-semibold"
                : "border-transparent hover:bg-panel/40",
            )}
          >
            <span className="size-2 rounded-sm bg-foreground" />
            <span className="flex-1">All global flags</span>
            <span className="font-mono text-xs text-faint">{globals.length}</span>
          </button>

          <div className="border-t border-rule mt-3 pt-2">
            <div className="px-4 py-2 text-eyebrow">Groups</div>
            {groups.length === 0 ? (
              <div className="px-4 py-3 text-xs text-muted-foreground italic">
                No groups yet.
              </div>
            ) : (
              groups.map((g) => {
                const active =
                  selection.kind === "group" && selection.groupId === g.id;
                return (
                  <button
                    key={g.id}
                    onClick={() =>
                      setSelection({ kind: "group", groupId: g.id })
                    }
                    className={cn(
                      "w-full text-left px-4 py-2 flex items-center gap-2 text-sm border-l-2",
                      active
                        ? "bg-panel border-foreground font-semibold"
                        : "border-transparent hover:bg-panel/40",
                    )}
                  >
                    <span
                      className="size-2 rounded-sm"
                      style={{ background: g.color }}
                    />
                    <span className="flex-1 truncate">{g.name}</span>
                    <span className="font-mono text-xs text-faint">
                      {g.prefix}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Sheet */}
        <div className="overflow-auto">
          {/* Header + env switcher */}
          <div className="px-6 py-4 border-b border-rule bg-paper sticky top-0 z-10">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="font-serif text-2xl font-semibold tracking-tight">
                {selection.kind === "global"
                  ? "Global flags"
                  : currentGroup?.name ?? "…"}
              </h2>
              <span className="font-mono text-xs text-faint">
                {selection.kind === "global"
                  ? `${globals.length} flags`
                  : `${groupFlags.length} flags · ${records.length} records`}
              </span>
            </div>
            <div className="mt-3 flex gap-1 items-center">
              <span className="text-eyebrow mr-2">Env</span>
              {environments.map((e) => (
                <button
                  key={e.id}
                  onClick={() => setEnvKey(e.key)}
                  className={cn(
                    "px-2.5 py-1 rounded-md text-xs font-mono border",
                    envKey === e.key
                      ? "bg-ink text-paper border-ink"
                      : "border-rule bg-card-paper text-muted-foreground",
                  )}
                >
                  {e.key}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          {selection.kind === "global" ? (
            <GlobalFlagsTable
              flags={globals}
              loading={flagsLoading}
              onUpdate={updateFlagOptimistic}
              onDelete={deleteFlag}
            />
          ) : (
            <GroupSheet
              flags={groupFlags}
              records={records}
              recordValues={recordValues}
              loading={groupDetailsLoading}
              onAddRecord={() => setShowCreateRecord(true)}
              onAddFlag={() => setShowCreateGroupFlag(true)}
              onSetValue={setRecordValueOptimistic}
            />
          )}
        </div>
      </div>

      {showCreateFlag && (
        <CreateGlobalFlagDialog
          projectId={projectId!}
          envKey={envKey}
          onClose={() => setShowCreateFlag(false)}
          onCreated={() => {
            setShowCreateFlag(false);
            refreshFlags();
          }}
        />
      )}
      {showCreateGroup && (
        <CreateGroupDialog
          projectId={projectId!}
          onClose={() => setShowCreateGroup(false)}
          onCreated={() => {
            setShowCreateGroup(false);
            refreshFlags();
          }}
        />
      )}
      {showCreateGroupFlag && selection.kind === "group" && (
        <CreateGroupFlagDialog
          projectId={projectId!}
          groupId={selection.groupId}
          onClose={() => setShowCreateGroupFlag(false)}
          onCreated={() => {
            setShowCreateGroupFlag(false);
            refreshGroupDetails(selection.groupId);
          }}
        />
      )}
      {showCreateEnv && (
        <CreateEnvironmentDialog
          projectId={projectId!}
          onClose={() => setShowCreateEnv(false)}
          onCreated={() => {
            setShowCreateEnv(false);
            refreshEnvs();
          }}
        />
      )}
      {showCreateRecord && selection.kind === "group" && (
        <CreateRecordDialog
          projectId={projectId!}
          groupId={selection.groupId}
          onClose={() => setShowCreateRecord(false)}
          onCreated={() => {
            setShowCreateRecord(false);
            refreshGroupDetails(selection.groupId);
          }}
        />
      )}
    </>
  );
}

function GlobalFlagsTable({
  flags,
  loading,
  onUpdate,
  onDelete,
}: {
  flags: GlobalFlag[];
  loading: boolean;
  onUpdate: (flag: GlobalFlag, patch: Partial<GlobalFlag>) => Promise<void>;
  onDelete: (flag: GlobalFlag) => Promise<void>;
}) {
  if (loading && flags.length === 0) {
    return (
      <div className="px-6 py-12 text-sm text-muted-foreground italic text-center">
        Loading flags…
      </div>
    );
  }
  if (flags.length === 0) {
    return (
      <div className="px-6 py-12 text-sm text-muted-foreground italic text-center">
        No global flags in this environment.
      </div>
    );
  }
  return (
    <div className={cn("relative", loading && "opacity-60")}>
      {loading && (
        <div className="absolute top-2 right-4 text-xs font-mono text-muted-foreground">
          Loading…
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-rule bg-card-paper">
            <th className="text-left px-6 py-3 text-eyebrow">Flag</th>
            <th className="text-left px-3 py-3 text-eyebrow">Key</th>
            <th className="text-left px-3 py-3 text-eyebrow">Enabled</th>
            <th className="text-left px-3 py-3 text-eyebrow">Rollout</th>
            <th className="px-3 py-3" />
          </tr>
        </thead>
        <tbody>
          {flags.map((f) => (
            <GlobalFlagRow
              key={f.id}
              flag={f}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GlobalFlagRow({
  flag,
  onUpdate,
  onDelete,
}: {
  flag: GlobalFlag;
  onUpdate: (flag: GlobalFlag, patch: Partial<GlobalFlag>) => Promise<void>;
  onDelete: (flag: GlobalFlag) => Promise<void>;
}) {
  // Local slider state so dragging is smooth; commit fires the request.
  const [localPct, setLocalPct] = useState(flag.rollout);
  useEffect(() => {
    setLocalPct(flag.rollout);
  }, [flag.rollout]);

  return (
    <tr className="border-b border-rule-soft">
      <td className="px-6 py-3 font-medium">{flag.name}</td>
      <td className="px-3 py-3 font-mono text-xs text-muted-foreground">
        {flag.key}
      </td>
      <td className="px-3 py-3">
        <Switch
          checked={flag.enabled}
          onCheckedChange={(v) => onUpdate(flag, { enabled: v })}
        />
      </td>
      <td className="px-3 py-3 w-[200px]">
        <div className="flex items-center gap-2">
          <Slider
            value={[localPct]}
            onValueChange={([v]) => setLocalPct(v!)}
            onValueCommit={([v]) => onUpdate(flag, { rollout: v! })}
            max={100}
            className="flex-1"
          />
          <span className="font-mono text-xs w-10 text-right">{localPct}%</span>
        </div>
      </td>
      <td className="px-3 py-3 text-right">
        <Button variant="ghost" size="sm" onClick={() => onDelete(flag)}>
          Delete
        </Button>
      </td>
    </tr>
  );
}

function GroupSheet({
  flags,
  records,
  recordValues,
  loading,
  onSetValue,
  onAddRecord,
  onAddFlag,
}: {
  flags: GroupFlag[];
  records: GroupRecord[];
  recordValues: Record<string, RecordValue[]>;
  loading: boolean;
  onSetValue: (
    recordId: string,
    groupFlagId: string,
    enabled: boolean,
    percentage: number,
  ) => Promise<void>;
  onAddRecord: () => void;
  onAddFlag: () => void;
}) {
  if (loading && flags.length === 0 && records.length === 0) {
    return (
      <div className="px-6 py-12 text-sm text-muted-foreground italic text-center">
        Loading group…
      </div>
    );
  }
  if (flags.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <div className="text-sm text-muted-foreground italic mb-3">
          No flags in this group yet.
        </div>
        <Button onClick={onAddFlag}>+ Add flag</Button>
      </div>
    );
  }
  return (
    <div className={cn("overflow-x-auto relative", loading && "opacity-60")}>
      {loading && (
        <div className="absolute top-2 right-4 text-xs font-mono text-muted-foreground z-20">
          Loading…
        </div>
      )}
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-card-paper">
            <th className="sticky left-0 z-10 bg-card-paper text-left px-6 py-3 text-eyebrow border-b border-r border-rule min-w-[200px]">
              Record
            </th>
            {flags.map((f) => (
              <th
                key={f.id}
                className="text-left px-3 py-3 border-b border-r border-rule-soft min-w-[150px]"
              >
                <div className="font-mono text-[11px] font-bold text-foreground">
                  {f.key}
                </div>
                <div className="font-mono text-[10px] text-faint">
                  default {f.defaultEnabled ? "true" : "false"}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((r) => {
            const valuesByFlag = new Map(
              (recordValues[r.id] ?? []).map((v) => [v.groupFlagId, v]),
            );
            return (
              <tr key={r.id} className="hover:bg-panel/30">
                <td className="sticky left-0 bg-paper z-10 px-6 py-3 border-b border-r border-rule font-mono text-xs">
                  <div className="font-semibold">{r.name ?? r.id}</div>
                  <div className="text-faint text-[10px]">{r.id}</div>
                </td>
                {flags.map((f) => {
                  const v = valuesByFlag.get(f.id);
                  const enabled = v?.enabled ?? f.defaultEnabled;
                  const pct = v?.percentage ?? f.defaultPercentage;
                  const isOverride = Boolean(v);
                  return (
                    <td
                      key={f.id}
                      className={cn(
                        "px-3 py-3 border-b border-r border-rule-soft",
                        isOverride &&
                          "bg-signal-red-soft/30 border-l-2 border-l-signal-red",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={enabled}
                          onCheckedChange={(checked) =>
                            onSetValue(r.id, f.id, checked, pct)
                          }
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
          <tr>
            <td
              colSpan={flags.length + 1}
              className="px-6 py-3 text-xs font-mono text-signal-red cursor-pointer border-b border-rule-soft"
              onClick={onAddRecord}
            >
              + new record
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── Dialogs ───────────────────────────────────────────────────────────

function CreateGlobalFlagDialog({
  projectId,
  envKey,
  onClose,
  onCreated,
}: {
  projectId: string;
  envKey: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`/api/projects/${projectId}/flags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, key, environmentKey: envKey }),
      });
      if (!res.ok) {
        setError((await res.json()).error ?? "Failed");
        return;
      }
      onCreated();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New global flag</DialogTitle>
          <DialogDescription>
            Scoped to env <b>{envKey}</b>. Leave key blank to auto-generate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Key (optional)</Label>
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="auto-generated from name"
              disabled={submitting}
            />
          </div>
          {error && <div className="text-sm text-signal-red">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name || submitting}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateGroupDialog({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [prefixTouched, setPrefixTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const autoPrefix = useMemo(() => derivePrefix(name), [name]);
  const effectivePrefix = prefixTouched ? prefix : autoPrefix;

  const submit = async () => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`/api/projects/${projectId}/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, prefix: effectivePrefix }),
      });
      if (!res.ok) {
        setError((await res.json()).error ?? "Failed");
        return;
      }
      onCreated();
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New group</DialogTitle>
          <DialogDescription>
            Groups hold per-record flag overrides. The prefix is used in record
            IDs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Prefix (2-5 chars)</Label>
            <Input
              value={effectivePrefix}
              onChange={(e) => {
                setPrefixTouched(true);
                setPrefix(e.target.value);
              }}
              placeholder="ent"
              disabled={submitting}
            />
          </div>
          {error && <div className="text-sm text-signal-red">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!name || !effectivePrefix || submitting}
          >
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateGroupFlagDialog({
  projectId,
  groupId,
  onClose,
  onCreated,
}: {
  projectId: string;
  groupId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(
        `/api/projects/${projectId}/groups/${groupId}/flags`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, key }),
        },
      );
      if (!res.ok) {
        setError((await res.json()).error ?? "Failed");
        return;
      }
      onCreated();
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New flag in group</DialogTitle>
          <DialogDescription>
            Adds a column to the sheet. Leave key blank to auto-generate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Key (optional)</Label>
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              disabled={submitting}
            />
          </div>
          {error && <div className="text-sm text-signal-red">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name || submitting}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateEnvironmentDialog({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`/api/projects/${projectId}/environments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, key, isDefault }),
      });
      if (!res.ok) {
        setError((await res.json()).error ?? "Failed");
        return;
      }
      onCreated();
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New environment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Staging"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Key (optional)</Label>
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="staging"
              disabled={submitting}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Make default</Label>
            <Switch
              checked={isDefault}
              onCheckedChange={setIsDefault}
              disabled={submitting}
            />
          </div>
          {error && <div className="text-sm text-signal-red">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name || submitting}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateRecordDialog({
  projectId,
  groupId,
  onClose,
  onCreated,
}: {
  projectId: string;
  groupId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [customId, setCustomId] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const trimmedId = customId.trim();
      const res = await fetch(
        `/api/projects/${projectId}/groups/${groupId}/records`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...(trimmedId ? { id: trimmedId } : {}),
            name,
          }),
        },
      );
      if (!res.ok) {
        setError((await res.json()).error ?? "Failed");
        return;
      }
      onCreated();
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New record</DialogTitle>
          <DialogDescription>
            A row in the sheet. Provide your own ID to match an internal entity,
            or leave blank to auto-generate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>ID (optional)</Label>
            <Input
              value={customId}
              onChange={(e) => setCustomId(e.target.value)}
              placeholder="e.g. user_42"
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Use your own internal ID (letters, digits, '-' or '_'). Leave
              blank to auto-generate.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Name (optional)</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
            />
          </div>
          {error && <div className="text-sm text-signal-red">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
